import React, { Component, ErrorInfo, ReactNode } from 'react';
import BaseApi from '../Core/BaseApi';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    isChunkError: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            isChunkError: false
        };
    }

    static isChunkLoadError(error: Error | null): boolean {
        if (!error) return false;
        const msg = (error.message || '').toLowerCase();
        const name = (error.name || '').toLowerCase();
        return (
            name === 'chunkloaderror' ||
            msg.includes('loading chunk') ||
            msg.includes('chunkloaderror') ||
            msg.includes('failed to fetch dynamically imported module') ||
            msg.includes('unexpected token <') ||
            msg.includes("unexpected token '<'")
        );
    }

    static getDerivedStateFromError(error: Error): State {
        const isChunkError = ErrorBoundary.isChunkLoadError(error);
        return {
            hasError: true,
            error,
            isChunkError
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        BaseApi.ReportClientException(
            error,
            `${error.stack || ''}\n${errorInfo.componentStack || ''}`
        );
        console.error('[ErrorBoundary] Caught application error:', error, errorInfo);

        // If it is a chunk load error (usually caused by a new CI/CD deployment),
        // automatically trigger a one-time clean reload to get fresh assets.
        if (ErrorBoundary.isChunkLoadError(error)) {
            const lastReload = sessionStorage.getItem('chunk_reload_ts');
            const now = Date.now();
            if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
                sessionStorage.setItem('chunk_reload_ts', now.toString());
                console.warn('[ErrorBoundary] ChunkLoadError detected. Performing automatic reload for updated assets...');
                window.location.reload();
            }
        }
    }

    handleReload = () => {
        sessionStorage.removeItem('chunk_reload_ts');
        window.location.reload();
    };

    handleHome = () => {
        window.location.href = '/admin/dashboard';
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    padding: '2rem',
                    textAlign: 'center',
                    fontFamily: 'Inter, Poppins, Roboto, sans-serif'
                }}>
                    <div style={{
                        maxWidth: 480,
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 12,
                        padding: '2rem',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                            {this.state.isChunkError ? '🔄' : '⚠️'}
                        </div>
                        <h2 style={{ margin: '0 0 0.5rem 0', color: '#212121', fontSize: '1.25rem', fontWeight: 600 }}>
                            {this.state.isChunkError ? 'New Version Available' : 'Something went wrong'}
                        </h2>
                        <p style={{ margin: '0 0 1.5rem 0', color: '#616161', fontSize: '0.875rem', lineHeight: 1.5 }}>
                            {this.state.isChunkError
                                ? 'The application was recently updated on the server. Please reload to load the latest version.'
                                : 'An unexpected error occurred while rendering this page.'}
                        </p>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                            <button
                                onClick={this.handleReload}
                                style={{
                                    backgroundColor: '#2196f3',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: 8,
                                    padding: '0.625rem 1.25rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                            >
                                Reload Page
                            </button>
                            <button
                                onClick={this.handleHome}
                                style={{
                                    backgroundColor: '#f5f5f5',
                                    color: '#424242',
                                    border: '1px solid #dcdcdc',
                                    borderRadius: 8,
                                    padding: '0.625rem 1.25rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                            >
                                Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
